import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DevicesListFilter from 'components/DevicesFilter/DevicesListFilter';
import CustomSnackbar from 'components/CustomSnackbar';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import DashboardLayout from 'layouts/LayoutContainers/DashboardLayout';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserDevices } from 'stores/sensor/sensorStore';
import { triggerSolinUpdater } from 'services/adminCenterService';
import { brandColors } from 'types/colors';

const selectionCheckboxSx = {
  '& .MuiSvgIcon-root': {
    border: `2px solid ${brandColors.GREY_DARK}`,
  },
  '&.Mui-checked .MuiSvgIcon-root, &.MuiCheckbox-indeterminate .MuiSvgIcon-root': {
    borderColor: brandColors.BLUE,
  },
};

const getLocation = device =>
  [
    device.building,
    device.floor_number !== null && device.floor_number !== undefined ? `Floor ${device.floor_number}` : null,
    device.area_name,
  ]
    .filter(Boolean)
    .join(' / ');

export const SolinUpdaterTrigger = () => {
  const dispatch = useDispatch();
  const devices = useSelector(state => state.sensors.filteredDevices || []);
  const allDevices = useSelector(state => state.sensors.devices || []);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    dispatch(fetchUserDevices({ devicesType: 'all' }));
  }, [dispatch]);

  const visibleDeviceIds = useMemo(() => devices.map(device => Number(device.id)).filter(Number.isSafeInteger), [devices]);
  const visibleDeviceIdSet = useMemo(() => new Set(visibleDeviceIds), [visibleDeviceIds]);
  const selectedVisibleCount = selectedDeviceIds.filter(deviceId => visibleDeviceIdSet.has(deviceId)).length;
  const allVisibleSelected = visibleDeviceIds.length > 0 && selectedVisibleCount === visibleDeviceIds.length;
  const partiallyVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

  const toggleDevice = deviceId => {
    setSelectedDeviceIds(currentIds =>
      currentIds.includes(deviceId) ? currentIds.filter(currentId => currentId !== deviceId) : [...currentIds, deviceId],
    );
  };

  const toggleAllVisible = () => {
    setSelectedDeviceIds(currentIds => {
      if (allVisibleSelected) {
        // Remove only the devices shown by the current filter. Keep selections
        // made through earlier searches.
        return currentIds.filter(deviceId => !visibleDeviceIdSet.has(deviceId));
      }

      // Add the currently filtered devices without losing earlier selections.
      return [...new Set([...currentIds, ...visibleDeviceIds])];
    });
  };

  const triggerSelectedDevices = async () => {
    if (selectedDeviceIds.length === 0) return;
    setError(null);
    setIsSubmitting(true);
    try {
      const response = await triggerSolinUpdater(selectedDeviceIds);
      const unsuccessfulCount =
        (response?.skipped?.length || 0) + (response?.failed?.length || 0) + (response?.missing_device_ids?.length || 0);
      setSnack({
        open: true,
        message: `Immediate solin-update triggered.${
          unsuccessfulCount ? ` ${unsuccessfulCount} device request${unsuccessfulCount === 1 ? '' : 's'} could not be queued.` : ''
        }`,
        severity: unsuccessfulCount ? 'warning' : 'success',
      });
    } catch (requestError) {
      setError(requestError.message || 'Could not request the immediate updater run');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <CustomSnackbar
        {...snack}
        onClose={() => setSnack(current => ({ ...current, open: false }))}
        duration={3000}
        vertical="top"
        horizontal="left"
        color="#fff"
      />
      <DashboardNavbar objectName="Immediate Updates" />
      <Box sx={{ mt: 3, mb: 2, px: { xs: 0, sm: 1.5 } }}>
        <Typography sx={{ color: '#152536', fontSize: 28, fontWeight: 800, lineHeight: 1.2 }}>Run Solin updater now</Typography>
        <Typography sx={{ color: '#6d7178', fontSize: 15, fontWeight: 500, mt: 0.75 }}>
          Select all filtered devices or choose individual devices. An MQTT request starts the updater instead of waiting for the next hour.
        </Typography>
      </Box>

      <Box sx={{ mt: 2 }}>
        <DevicesListFilter />
      </Box>

      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 2, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          {devices.length} of {allDevices.length} devices shown · {selectedDeviceIds.length} selected
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="contained"
            onClick={triggerSelectedDevices}
            disabled={selectedDeviceIds.length === 0 || isSubmitting}
            sx={{
              backgroundColor: brandColors.BLUE,
              color: '#fff',
              '&:hover': { backgroundColor: brandColors.BLUE },
            }}>
            {isSubmitting ? <CircularProgress size={20} color="inherit" /> : `Run updater now (${selectedDeviceIds.length})`}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allVisibleSelected}
                  indeterminate={partiallyVisibleSelected}
                  onChange={toggleAllVisible}
                  inputProps={{ 'aria-label': 'Select all filtered devices' }}
                  sx={selectionCheckboxSx}
                />
              </TableCell>
              <TableCell>Device</TableCell>
              <TableCell>MAC address</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Type</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {devices.map(device => {
              const deviceId = Number(device.id);
              const selected = selectedDeviceIds.includes(deviceId);
              return (
                <TableRow hover key={device.id} selected={selected}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selected}
                      onChange={() => toggleDevice(deviceId)}
                      inputProps={{ 'aria-label': `Select ${device.name || device.id}` }}
                      sx={selectionCheckboxSx}
                    />
                  </TableCell>
                  <TableCell>{device.name || `Device ${device.id}`}</TableCell>
                  <TableCell>{device.mac || '—'}</TableCell>
                  <TableCell>{getLocation(device) || '—'}</TableCell>
                  <TableCell>{device.device_type || '—'}</TableCell>
                </TableRow>
              );
            })}
            {devices.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No devices match the current filters.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </DashboardLayout>
  );
};

export default SolinUpdaterTrigger;
